/**
 * Хук для работы с пользователями: список, создание, обновление, удаление, активация/деактивация
 * Дата: 28.12.2025 - Создан для TODO-013 (Frontend страницы + WhatsApp интеграция)
 * Дата: 29.12.2025 - Добавлена поддержка tenantId для SuperAdmin
 * Связан с: frontend/src/api/users.ts, frontend/src/pages/Users.tsx
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, type UpdateUserRequest } from '@/api/users';

export function useUsers(tenantId?: string) {
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['users', tenantId],
    queryFn: () => usersApi.list(tenantId),
    enabled: !!tenantId, // Запрос выполняется только если есть tenantId
  });

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      usersApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      isActive ? usersApi.deactivate(id) : usersApi.activate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  return {
    users: usersQuery.data?.users || [],
    total: usersQuery.data?.total || 0,
    isLoading: usersQuery.isLoading,
    createUser: createMutation.mutateAsync,
    updateUser: updateMutation.mutateAsync,
    deleteUser: deleteMutation.mutateAsync,
    toggleActive: toggleActiveMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

